import { defineStore } from "pinia";
import axiosInstance from "../config/axios";

export const useAnalyticsStore = defineStore("analytics", {
    state: () => {
        return {
            abortActions: {},
            numberOfProducts: 0,
            clicksPerDay: [],
            costPerClickAveragePerDay: [],
            costPerClickPerDay: [],
            bidSharePerDay: [],
            bidCpcPerDay: [],
            liquidityData: [],
            liquidityLoading: false,
            topProducts: [],
            topProductsPerPage: 50,
            topProductsPage: 1,
            topProductsClicks: [],
            topProductsAvgCpc: [],
            topProductsCpc: [],
            topProductsBidShare: [],
            topProductsLoading: false,
            topProductsClicksLoading: false,
            topProductsSortBy: null,
            topProductsSortOrder: null,
            topCategories: [],
            topCategoriesPerPage: 50,
            topCategoriesPage: 1,
            topCategoriesClicks: [],
            topCategoriesCpcAvg: [],
            topCategoriesCpc: [],
            topCategoriesLoading: false,
            topCategoriesClicksLoading: false,
            topCategoriesSortBy: null,
            topCategoriesSortOrder: null,
            topCategoriesV2: [],
            devices: [],
            devicesPerDay: [],
            devicesLoading: false,
            dayHour: [],
            dayHourPerDay: [],
            dayHourLoading: false,
            categories: [],
            categoriesPerPage: 10,
            categoriesPage: 1,
            categoriesLoading: false,
            categoriesSortBy: null,
            categoriesSortOrder: null,
            categoriesBiddings: [],
            categoriesBiddingsLoading: false,
            categoriesBiddingsSortBy: null,
            categoriesBiddingsOrderBy: null,
            clicks: [],
            clicksPerPage: 50,
            clicksPage: 1,
            clickClicksLoading: false,
            clickLoading: false,
            clickSortBy: null,
            clickOrderBy: null,
            daysMap: {
                Monday: 0,
                Tuesday: 1,
                Wednesday: 2,
                Thursday: 3,
                Friday: 4,
                Saturday: 5,
                Sunday: 6,
            },
            categoriesByLevel1: [],
            categoriesByLevel2: [],
            categoriesByLevel3: [],
            currentSelectedType: null,
            currentSelectedId: null,
            marketBrands: [],
            marketBrandsPerPage: 10,
            marketBrandsPage: 1,
            marketProducts: [],
            marketProductsPerPage: 10,
            marketProductsPage: 1,
            marketCategories: [],
            marketCategoriesPerPage: 10,
            marketCategoriesPage: 1,
            marketLoading: false,
            marketBrandsLoading: false,
            marketCategoriesLoading: false,
            marketProductsLoading: false,
            selectedGroupBy: { name: "Day", code: "day" },
            groupByOptions: [
                { name: "Day", code: "day" },
                { name: "Week", code: "week" },
                { name: "Month", code: "month" },
            ],
            avgProductPrice: 0,
            potentialRevenue: 0,
            graphDataLading: false,
            devicesClicksLoading: false,
            dayHourClicksLoading: false,
            dayHourSortOrder: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
            offersData: [],
            offersCategoriesDistribution: [],
            clicksPositionDistribution: [],
            distributionLoading: false,
            selectedLevel1: null,
            selectedLevel2: null,
            selectedLevel3: null,
            plotItems: [],
            dailyMetrics: [],
            dailyMetricsLoading: false,
            dailyTopProductsMetrics: [],
            dailyTopProductsMetricsLoading: false,
            dailyBidMetrics: [],
            dailyBidMetricsLoading: false,
        };
    },
    getters: {
        clicksSum(state) {
            const clicks = state.clicksPerDay.map((value) => (value.clicks ? value.clicks : 0));
            return clicks.reduce((previousValue, currentValue) => previousValue + currentValue, 0);
        },
        clicksSumV2(state) {
            const clicks = state.dailyMetrics.map((value) => (value.clicks ? value.clicks : 0));
            return clicks.reduce((previousValue, currentValue) => previousValue + currentValue, 0);
        },
        cpcAverage(state) {
            const cpcData = state.costPerClickAveragePerDay.filter((value) => {
                return value.cpc_avg > 0;
            });

            const cpcAverage = cpcData.map((value) => value.cpc_avg);

            return cpcAverage.length !== 0
                ? cpcAverage.reduce((previousValue, currentValue) => previousValue + currentValue, 0) /
                      cpcAverage.length
                : 0;
        },
        cpcAverageV2(state) {
            const cpcData = state.dailyMetrics.filter((value) => {
                return parseFloat(value.cpc_avg) > 0;
            });

            const cpcAverage = cpcData.map((value) => parseFloat(value.cpc_avg));

            return cpcAverage.length !== 0
                ? cpcAverage.reduce((previousValue, currentValue) => previousValue + currentValue, 0) /
                      cpcAverage.length
                : 0;
        },
        cpcSum(state) {
            const cpc = state.costPerClickPerDay.map((value) => (value.cpc ? value.cpc : 0));
            return cpc.reduce((previousValue, currentValue) => previousValue + currentValue, 0).toFixed(2);
        },
        cpcSumV2(state) {
            const cpc = state.dailyMetrics.map((value) => parseFloat(value.cpc_sum ? value.cpc_sum : 0));
            return cpc.reduce((previousValue, currentValue) => previousValue + currentValue, 0);
        },
        bidShareSum(state) {
            const bidShare = state.bidSharePerDay.map((value) => (value.bid_clicks ? value.bid_clicks : 0));
            return bidShare.reduce((previousValue, currentValue) => previousValue + currentValue, 0);
        },
        bidClicksSum(state) {
            const bidClicks = state.dailyMetrics.map((value) => (value.bid_clicks ? value.bid_clicks : 0));
            return bidClicks.reduce((previousValue, currentValue) => previousValue + currentValue, 0);
        },
        bidClicks(state) {
            return state.bidSharePerDay;
        },
        offersAvg(state) {
            const offers = state.offersData.map((value) => (value.num_offers ? value.num_offers : 0));

            let offersSum = offers.reduce((previousValue, currentValue) => previousValue + currentValue, 0);
            let offersLength = offers.reduce(
                (previousValue, currentValue) => (currentValue !== 0 ? previousValue + 1 : previousValue + 0),
                0
            );

            return offersSum && offersLength ? (offersSum / offersLength).toFixed(0) : 0;
        },
        liquiditySum(state) {
            const clicksSum = state.liquidityData.reduce(
                (previousValue, currentValue) => previousValue + currentValue.num_clicks,
                0
            );

            const offersSum = state.liquidityData.reduce(
                (previousValue, currentValue) => previousValue + currentValue.num_offers,
                0
            );

            return clicksSum & offersSum ? ((clicksSum / offersSum) * 100).toFixed(2) : 0;
        },
        dayHourPerDayClicksSum(state) {
            return state.dayHourPerDay.reduce((previousValue, currentValue) => previousValue + currentValue.clicks, 0);
        },
    },
    actions: {
        fetchNumberOfProductsAnalytics({ shopId, data }) {
            return axiosInstance
                .get(`/v1/analytics/num-products/${shopId}`, {
                    params: data,
                    signal: this.abortActions["fetchNumberOfProductsAnalytics"].signal,
                })
                .then((response) => {
                    if (response !== undefined) {
                        this.numberOfProducts = response.data;
                    }
                });
        },
        fetchClicksPerDayAnalytics({ shopId, data }) {
            return axiosInstance
                .get(`/v1/analytics/num-clicks/${shopId}`, {
                    params: data,
                    signal: this.abortActions["fetchClicksPerDayAnalytics"].signal,
                })
                .then((response) => {
                    if (response !== undefined) {
                        this.clicksPerDay = response.data;
                    }
                });
        },
        async fetchClicksPerDayAnalyticsV2({ shopId, data }) {
            return axiosInstance
                .get(`/v1/analytics/num-clicksV2/${shopId}`, {
                    params: data,
                    signal: this.abortActions["fetchClicksPerDayAnalyticsV2"].signal,
                })
                .then((response) => (this.clicksPerDay = response.data));
        },
        async fetchClicksPerDayAnalyticsPlotV2({ shopId, data }) {
            return axiosInstance.get(`/v1/analytics/num-clicksV2/${shopId}`, {
                params: data,
                signal: this.abortActions["fetchClicksPerDayAnalyticsPlotV2"].signal,
            });
        },
        fetchCostPerClickAveragePerDayAnalytics({ shopId, data }) {
            return axiosInstance
                .get(`/v1/analytics/cpc-avg/${shopId}`, {
                    params: data,
                    signal: this.abortActions["fetchCostPerClickAveragePerDayAnalytics"].signal,
                })
                .then((response) => {
                    if (response !== undefined) {
                        this.costPerClickAveragePerDay = response.data.map((value) => ({
                            date: value.date,
                            cpc_avg: value.cpc_avg ? parseFloat(value.cpc_avg) : value.cpc_avg,
                        }));
                    }
                });
        },
        async fetchCostPerClickAveragePerDayAnalyticsV2({ shopId, data }) {
            return axiosInstance
                .get(`/v1/analytics/cpc-avgV2/${shopId}`, {
                    params: data,
                    signal: this.abortActions["fetchCostPerClickAveragePerDayAnalyticsV2"].signal,
                })
                .then(
                    (response) =>
                        (this.costPerClickAveragePerDay = response.data.map((value) => ({
                            date: value.date,
                            cpc_avg: value.cpc_avg ? parseFloat(value.cpc_avg) : value.cpc_avg,
                        })))
                );
        },
        async fetchCostPerClickAveragePerDayAnalyticsPlotV2({ shopId, data }) {
            return axiosInstance.get(`/v1/analytics/cpc-avgV2/${shopId}`, {
                params: data,
                signal: this.abortActions["fetchCostPerClickAveragePerDayAnalyticsPlotV2"].signal,
            });
        },
        fetchCostPerClickPerDayAnalytics({ shopId, data }) {
            return axiosInstance
                .get(`/v1/analytics/cpc/${shopId}`, {
                    params: data,
                    signal: this.abortActions["fetchCostPerClickPerDayAnalytics"].signal,
                })
                .then((response) => {
                    if (response !== undefined) {
                        this.costPerClickPerDay = response.data.map((value) => ({
                            date: value.date,
                            cpc: value.cpc ? parseFloat(value.cpc) : value.cpc,
                        }));
                    }
                });
        },
        async fetchCostPerClickPerDayAnalyticsV2({ shopId, data }) {
            return axiosInstance
                .get(`/v1/analytics/cpcV2/${shopId}`, {
                    params: data,
                    signal: this.abortActions["fetchCostPerClickPerDayAnalyticsV2"].signal,
                })
                .then(
                    (response) =>
                        (this.costPerClickPerDay = response.data.map((value) => ({
                            date: value.date,
                            cpc: value.cpc ? parseFloat(value.cpc) : value.cpc,
                        })))
                );
        },
        async fetchCostPerClickPerDayAnalyticsPlotV2({ shopId, data }) {
            return axiosInstance.get(`/v1/analytics/cpcV2/${shopId}`, {
                params: data,
                signal: this.abortActions["fetchCostPerClickPerDayAnalyticsPlotV2"].signal,
            });
        },
        fetchBidPerDayAnalytics({ shopId, data }) {
            return axiosInstance
                .get(`/v1/analytics/num-bid-clicks/${shopId}`, {
                    params: data,
                    signal: this.abortActions["fetchBidPerDayAnalytics"].signal,
                })
                .then((response) => (this.bidSharePerDay = response.data));
        },
        async fetchBidPerDayAnalyticsV2({ shopId, data }) {
            return axiosInstance
                .get(`/v1/analytics/num-bid-clicksV2/${shopId}`, {
                    params: data,
                    signal: this.abortActions["fetchBidPerDayAnalyticsV2"].signal,
                })
                .then((response) => (this.bidSharePerDay = response.data));
        },
        async fetchBidPerDayAnalyticsPlotV2({ shopId, data }) {
            return axiosInstance.get(`/v1/analytics/num-bid-clicksV2/${shopId}`, {
                params: data,
                signal: this.abortActions["fetchBidPerDayAnalyticsPlotV2"].signal,
            });
        },
        async fetchCostBidPerDayAnalytics({ shopId, data }) {
            return axiosInstance
                .get(`/v1/analytics/cpc-bid-clicks/${shopId}`, {
                    params: data,
                    signal: this.abortActions["fetchCostBidPerDayAnalytics"].signal,
                })
                .then((response) => (this.bidCpcPerDay = response.data));
        },
        async fetchCostBidPerDayAnalyticsV2({ shopId, data }) {
            return this.fetchCostBidPerDayAnalyticsPlotV2({ shopId, data }).then(
                (response) => (this.bidCpcPerDay = response.data)
            );
        },
        async fetchCostBidPerDayAnalyticsPlotV2({ shopId, data }) {
            return axiosInstance.get(`/v1/analytics/cpc-bid-clicksV2/${shopId}`, {
                params: data,
                signal: this.abortActions["fetchCostBidPerDayAnalyticsPlotV2"].signal,
            });
        },
        fetchLiquidityAnalytics({ shopId, data }) {
            return axiosInstance
                .get(`/v1/analytics/liquidity/${shopId}`, {
                    params: data,
                    signal: this.abortActions["fetchLiquidityAnalytics"].signal,
                })
                .then((response) => (this.liquidityData = response.data));
        },
        fetchTopProductsAnalytics({ shopId, data }) {
            return axiosInstance
                .get(`/v1/analytics/top-products/${shopId}`, {
                    params: data,
                    signal: this.abortActions["fetchTopProductsAnalytics"].signal,
                })
                .then((response) => {
                    if (response !== undefined) {
                        this.topProducts = response.data;
                        this.topProductsPerPage = response.data.per_page;
                        this.topProductsPage = response.data.current_page;
                    }
                });
        },
        fetchTopProductsClicksAnalytics({ shopId, data }) {
            return axiosInstance
                .get(`/v1/analytics/top-products-clicks/${shopId}`, {
                    params: data,
                    signal: this.abortActions["fetchTopProductsClicksAnalytics"].signal,
                })
                .then((response) => (this.topProductsClicks = response.data));
        },

        // Top products chart analytics
        async fetchTopProductsClicksAnalyticsV2({ shopId, data }) {
            return this.fetchTopProductsClicksAnalyticsPlotV2({ shopId, data }).then(
                (response) => (this.topProductsClicks = response.data)
            );
        },
        async fetchTopProductsClicksAnalyticsPlotV2({ shopId, data }) {
            return axiosInstance.get(`/v1/analytics/top-products-clicksV2/${shopId}`, {
                params: data,
                signal: this.abortActions["fetchTopProductsClicksAnalyticsPlotV2"].signal,
            });
        },

        async fetchTopProductsAvgCpcV2({ shopId, data }) {
            return this.fetchTopProductsAvgCpcPlotV2({ shopId, data }).then(
                (response) => (this.topProductsAvgCpc = response.data)
            );
        },
        async fetchTopProductsAvgCpcPlotV2({ shopId, data }) {
            return axiosInstance.get(`/v1/analytics/top-products-avg-cpcV2/${shopId}`, {
                params: data,
                signal: this.abortActions["fetchTopProductsAvgCpcPlotV2"].signal,
            });
        },

        async fetchTopProductsCpcV2({ shopId, data }) {
            return this.fetchTopProductsCpcPlotV2({ shopId, data }).then(
                (response) => (this.topProductsCpc = response.data)
            );
        },
        async fetchTopProductsCpcPlotV2({ shopId, data }) {
            return axiosInstance.get(`/v1/analytics/top-products-cpcV2/${shopId}`, {
                params: data,
                signal: this.abortActions["fetchTopProductsCpcPlotV2"].signal,
            });
        },

        async fetchTopProductsBidShareV2({ shopId, data }) {
            return this.fetchTopProductsBidSharePlotV2({ shopId, data }).then(
                (response) => (this.topProductsBidShare = response.data)
            );
        },
        async fetchTopProductsBidSharePlotV2({ shopId, data }) {
            return axiosInstance.get(`/v1/analytics/top-products-bid-clicksV2/${shopId}`, {
                params: data,
                signal: this.abortActions["fetchTopProductsBidSharePlotV2"].signal,
            });
        },

        async fetchTopProductsGraphData(graphData) {
            await this.fetchTopProductsClicksAnalyticsV2(graphData);

            return Promise.all([
                this.fetchTopProductsAvgCpcV2(graphData),
                this.fetchTopProductsCpcV2(graphData),
                this.fetchTopProductsBidShareV2(graphData),
            ]);
        },
        // Top products chart analytics

        fetchTopCategoriesAnalytics({ shopId, data }) {
            return axiosInstance
                .get(`/v1/analytics/top-categories/${shopId}`, {
                    params: data,
                    signal: this.abortActions["fetchTopCategoriesAnalytics"].signal,
                })
                .then((response) => {
                    if (response !== undefined) {
                        this.topCategories = response.data;
                        this.topCategoriesPerPage = response.data.per_page;
                        this.topCategoriesPage = response.data.current_page;
                    }
                });
        },
        async fetchTopCategoriesAnalyticsV2({ shopId, data }) {
            return axiosInstance
                .get(`/v1/analytics/top-categoriesV2/${shopId}`, {
                    params: data,
                    signal: this.abortActions["fetchTopCategoriesAnalyticsV2"].signal,
                })
                .then((response) => {
                    if (response !== undefined) {
                        this.topCategoriesV2 = response.data;
                    }
                });
        },
        fetchCategoriesByLevel({ shopId, data }) {
            return axiosInstance
                .get(`/v1/analytics/categories-by-level/${shopId}`, {
                    params: data,
                    signal: this.abortActions["fetchCategoriesByLevel"].signal,
                })
                .then((response) => {
                    if (!data) {
                        this.categoriesByLevel1 = response.data;
                    }

                    if (data?.category_type === "l1") {
                        this.categoriesByLevel2 = response.data;
                    } else if (data?.category_type === "l2") {
                        this.categoriesByLevel3 = response.data;
                    }
                });
        },
        fetchTopCategoriesByLevel({ shopId, data }) {
            return axiosInstance
                .get(`/v1/analytics/top-categories-by-level/${shopId}`, {
                    params: data,
                    signal: this.abortActions["fetchTopCategoriesByLevel"].signal,
                })
                .then((response) => {
                    if (response !== undefined) {
                        if (data?.category_type === "l2") {
                            this.categoriesByLevel3 = response.data;
                        } else if (data?.category_type === "l1") {
                            this.categoriesByLevel2 = response.data;
                        } else {
                            this.categoriesByLevel1 = response.data;
                        }
                    }
                });
        },
        fetchTopCategoriesClicksAnalytics({ shopId, data }) {
            return axiosInstance
                .get(`/v1/analytics/top-categories-clicks/${shopId}`, {
                    params: data,
                    signal: this.abortActions["fetchTopCategoriesClicksAnalytics"].signal,
                })
                .then((response) => (this.topCategoriesClicks = response.data));
        },
        async fetchTopCategoriesClicksAnalyticsPlotV2({ shopId, data }) {
            return axiosInstance.get(`/v1/analytics/top-categories-clicksV2/${shopId}`, {
                params: data,
                signal: this.abortActions["fetchTopCategoriesClicksAnalyticsPlotV2"].signal,
            });
        },
        async fetchTopCategoriesClicksAnalyticsV2({ shopId, data }) {
            return this.fetchTopCategoriesClicksAnalyticsPlotV2({ shopId, data }).then(
                (response) => (this.topCategoriesClicks = response.data)
            );
        },
        fetchDevicesAnalytics({ shopId, data }) {
            return axiosInstance
                .get(`/v1/analytics/devices/${shopId}`, {
                    params: data,
                    signal: this.abortActions["fetchDevicesAnalytics"].signal,
                })
                .then((response) => {
                    if (response !== undefined) {
                        this.devices = response.data;
                    }
                });
        },
        fetchDevicesPerDayAnalytics({ shopId, data }) {
            return axiosInstance
                .get(`/v1/analytics/devices-by-day/${shopId}`, {
                    params: data,
                    signal: this.abortActions["fetchDevicesPerDayAnalytics"].signal,
                })
                .then((response) => {
                    if (response !== undefined) {
                        this.devicesPerDay = response.data;
                    }
                });
        },
        fetchDayAndHourAnalytics({ shopId, data }) {
            return axiosInstance
                .get(`/v1/analytics/day-hour/${shopId}`, {
                    params: data,
                    signal: this.abortActions["fetchDayAndHourAnalytics"].signal,
                })
                .then((response) => {
                    if (response !== undefined) {
                        return (this.dayHour = response.data.map((value) => ({
                            hour: parseInt(value.hour_of_day),
                            date: this.daysMap[value.day_of_week.trim()],
                            clicks: value.clicks ? value.clicks : 0,
                        })));
                    }
                });
        },
        fetchDayAndHourPerDayAnalytics({ shopId, data }) {
            return axiosInstance
                .get(`/v1/analytics/day/${shopId}`, {
                    params: data,
                    signal: this.abortActions["fetchDayAndHourPerDayAnalytics"].signal,
                })
                .then((response) => {
                    if (response !== undefined) {
                        this.dayHourPerDay = response.data;
                    }
                });
        },
        fetchCategoriesAnalytics({ shopId, data }) {
            return axiosInstance
                .get(`/v1/analytics/categories/${shopId}`, {
                    params: data,
                    signal: this.abortActions["fetchCategoriesAnalytics"].signal,
                })
                .then((response) => {
                    if (response !== undefined) {
                        this.categories = response.data;
                        this.categoriesPerPage = response.data.per_page;
                        this.categoriesPage = response.data.current_page;
                    }
                });
        },
        fetchClicksAnalytics({ shopId, data }) {
            return axiosInstance
                .get(`/v1/analytics/clicks/${shopId}`, {
                    params: data,
                    signal: this.abortActions["fetchClicksAnalytics"].signal,
                })
                .then((response) => {
                    if (response !== undefined) {
                        this.clicks = response.data;
                        this.clicksPerPage = response.data.per_page;
                        this.clicksPage = response.data.current_page;
                    }
                });
        },

        fetchMarketCategoriesAnalytics({ data }) {
            return axiosInstance
                .get("/v1/analytics/top-categories", {
                    params: data,
                    signal: this.abortActions["fetchMarketCategoriesAnalytics"].signal,
                })
                .then((response) => {
                    this.marketCategories = response.data;
                    this.marketCategoriesPerPage = response.data.per_page;
                    this.marketCategoriesPage = response.data.current_page;
                });
        },

        fetchMarketProductsAnalytics({ data }) {
            return axiosInstance
                .get("/v1/analytics/top-products", {
                    params: data,
                    signal: this.abortActions["fetchMarketProductsAnalytics"].signal,
                })
                .then((response) => {
                    this.marketProducts = response.data;
                    this.marketProductsPerPage = response.data.per_page;
                    this.marketProductsPage = response.data.current_page;
                });
        },

        fetchMarketBrandsAnalytics({ data }) {
            return axiosInstance
                .get("/v1/analytics/top-brands", {
                    params: data,
                    signal: this.abortActions["fetchMarketBrandsAnalytics"].signal,
                })
                .then((response) => {
                    this.marketBrands = response.data;
                    this.marketBrandsPerPage = response.data.per_page;
                    this.marketBrandsPage = response.data.current_page;
                });
        },

        async fetchGraphData(graphData) {
            this.fetchOffers(graphData);
            await this.fetchClicksPerDayAnalytics(graphData);

            return Promise.all([
                this.fetchCostPerClickAveragePerDayAnalytics(graphData),
                this.fetchCostPerClickPerDayAnalytics(graphData),
                this.fetchBidPerDayAnalytics(graphData),
            ]);
        },

        async fetchTopCategoriesGraphDataV2(graphData) {
            await this.fetchTopCategoriesClicksAnalyticsV2(graphData);

            return Promise.all([
                this.fetchCostPerClickAveragePerDayAnalyticsV2(graphData),
                this.fetchCostPerClickPerDayAnalyticsV2(graphData),
                this.fetchBidPerDayAnalyticsV2(graphData),
            ]);
        },
        fetchAvgProductPrice({ shopId, data }) {
            return axiosInstance
                .get(`/v1/analytics/avg-products-price/${shopId}`, {
                    params: data,
                    signal: this.abortActions["fetchAvgProductPrice"].signal,
                })
                .then((response) => {
                    if (response !== undefined) {
                        this.avgProductPrice = response.data !== null ? parseFloat(response.data) : 0;
                    }
                });
        },

        fetchPotentialRevenue({ shopId, data }) {
            return axiosInstance
                .get(`/v1/analytics/potential-revenue/${shopId}`, {
                    params: data,
                    signal: this.abortActions["fetchPotentialRevenue"].signal,
                })
                .then((response) => {
                    this.potentialRevenue = response.data !== null ? parseFloat(response.data) : 0;
                });
        },

        fetchCategoriesBiddings({ shopId, data }) {
            return axiosInstance
                .get(`/v1/analytics/categories-biddings/${shopId}`, {
                    params: data,
                    signal: this.abortActions["fetchCategoriesBiddings"].signal,
                })
                .then((response) => {
                    if (response !== undefined) {
                        this.categoriesBiddings = response.data;
                    }
                });
        },
        fetchOffers({ shopId, data }) {
            return axiosInstance
                .get(`/v1/analytics/offers/${shopId}`, {
                    params: data,
                    signal: this.abortActions["fetchOffers"].signal,
                })
                .then((response) => {
                    this.offersData = response.data;
                });
        },
        async fetchOffersV2({ shopId, data }) {
            return axiosInstance
                .get(`/v1/analytics/offersV2/${shopId}`, {
                    params: data,
                    signal: this.abortActions["fetchOffersV2"].signal,
                })
                .then((response) => {
                    if (response !== undefined) {
                        this.offersData = response.data;
                    }
                });
        },
        async fetchOffersPlotV2({ shopId, data }) {
            return axiosInstance.get(`/v1/analytics/offersV2/${shopId}`, {
                params: data,
                signal: this.abortActions["fetchOffersPlotV2"].signal,
            });
        },
        fetchOffersCategoriesDistribution(shopId) {
            return axiosInstance.get(`/v1/analytics/offers-categories-distribution/${shopId}`).then((response) => {
                this.offersCategoriesDistribution = response.data;
            });
        },
        fetchClicksPositionsDistribution({ shopId, data }) {
            return axiosInstance
                .get(`/v1/analytics/clicks-positions-distribution/${shopId}`, {
                    params: data,
                    signal: this.abortActions["fetchClicksPositionsDistribution"].signal,
                })
                .then((response) => {
                    const positionOrder = ["Unique", "1", "2", "3", "4", "5+"];
                    this.clicksPositionDistribution = response.data.sort(
                        (a, b) => positionOrder.indexOf(a.position) - positionOrder.indexOf(b.position)
                    );
                });
        },

        // Daily metrics
        async fetchDailyMetrics({ shopId, data }) {
            return this.fetchDailyMetricsPlot({ shopId, data }).then((response) => {
                if (response !== undefined) {
                    this.dailyMetrics = response.data;
                }
            });
        },
        async fetchDailyMetricsPlot({ shopId, data }) {
            return axiosInstance.get(`/v1/analytics/daily-metrics/${shopId}`, {
                params: data,
                signal: this.abortActions["fetchDailyMetricsPlot"].signal,
            });
        },
        // Same endpoint as fetchDailyMetricsPlot but under its own action name so it gets
        // its own abort controller and does not cancel the current-period request.
        async fetchPreviousDailyMetrics({ shopId, data }) {
            return axiosInstance.get(`/v1/analytics/daily-metrics/${shopId}`, {
                params: data,
                signal: this.abortActions["fetchPreviousDailyMetrics"]?.signal,
            });
        },
        async fetchGraphDataV2(graphData) {
            await this.fetchDailyMetrics(graphData);
            await this.fetchOffersV2(graphData);
        },
        // Daily metrics
        // Top products daily metrics
        async fetchTopProductsDailyMetrics({ shopId, data }) {
            return this.fetchTopProductsDailyMetricsPlot({ shopId, data }).then((response) => {
                if (response !== undefined) {
                    this.dailyTopProductsMetrics = response.data;
                }
            });
        },
        async fetchTopProductsDailyMetricsPlot({ shopId, data }) {
            return axiosInstance.get(`/v1/analytics/top-products-daily-metric/${shopId}`, {
                params: data,
                signal: this.abortActions["fetchTopProductsDailyMetricsPlot"].signal,
            });
        },
        // Top products daily metrics
        // Daily bid metrics
        async fetchDailyBidMetrics({ shopId, data }) {
            return this.fetchDailyBidMetricsPlot({ shopId, data }).then((response) => {
                if (response !== undefined) {
                    this.dailyBidMetrics = response.data;
                }
            });
        },
        async fetchDailyBidMetricsPlot({ shopId, data }) {
            return axiosInstance.get(`/v1/analytics/daily-bid-metrics/${shopId}`, {
                params: data,
                signal: this.abortActions["fetchDailyBidMetricsPlot"].signal,
            });
        },
        // Daily bid metrics
    },
});
